import React, { useState } from "react";
import DraftsTable from "../components/DraftsTable";
import {
  Create,
  Edit,
  SimpleForm,
  List,
  Datagrid,
  TextField,
  TextInput,
  BooleanInput,
  Filter,
  Show,
  SaveButton,
  Toolbar,
  TabbedShowLayout,
  Tab,
  required,
  BooleanField,
  ReferenceField,
  ReferenceInput,
  AutocompleteInput,
  useRecordContext,
  usePermissions,
  ArrayField,
  DateField,
  ArrayInput,
  SimpleFormIterator,
  useListController,
  useGetList,
  Loading,
  useNotify,
  useRedirect,
  useDataProvider,
} from "react-admin";
import { useSearchParams } from "react-router-dom";
import { validateJSON } from "../api/validators";
import { useFormContext } from "react-hook-form";
import { Grid, Box, Alert, Button } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AppCreateDoc from "../documentation/AppCreateDoc";
import JSONPretty from "react-json-pretty";
import { permissionCheck, renderIfPermissions } from "../api/permissionManager";
import { Changelog } from "../components/Changelog";
import KeySwitchCreateButton from "../components/KeySwitchCreate";
import { CheckingPerms, LoadingList } from "../components/LoadingComponent";
import { WrappingTextField } from "../components/TruncatedTextField";

export const PrettyJsonField = (props) => {
  const { source } = props;
  const record = useRecordContext(props);

  if (!record || !record[source]) return null;

  return (
    <Box 
      sx={{ 
        maxWidth: '100%', 
        overflow: 'hidden', // Contains the element within the grid boundary
        '& pre': {
          whiteSpace: 'pre-wrap !important', 
          wordBreak: 'break-all !important', 
          overflowWrap: 'anywhere !important', // Forces long URLs to snap to the next line
          fontSize: 'clamp(0.65rem, 1.5vw, 0.9rem)',
        }
      }}
    >
      <JSONPretty id="json-pretty" data={record[source]} />
    </Box>
  );
};

const AppSaveButton = () => {
  const dataProvider = useDataProvider();
  const [isSaving, setIsSaving] = useState(false);
  const {
    handleSubmit,
    formState: { dirtyFields },
  } = useFormContext();
  const notify = useNotify();
  const redirect = useRedirect();

  const onSave = async (values) => {
    setIsSaving(true);
    const sensitiveFields = ["is_billable"];
    try {
      // split changes into sensitive and non sensitive
      const sensitiveUpdates = {};
      const nonSensitiveUpdates = {};
      Object.keys(dirtyFields).forEach((key) => {
        if (sensitiveFields.includes(key)) {
          sensitiveUpdates[key] = values[key];
        } else {
          let updated_value = values[key];
          // array parameter handling
          if (key == "android_signing_fingerprints" && updated_value) {
            updated_value = updated_value.split(",");
          }
          nonSensitiveUpdates[key] = updated_value;
        }
      });

      // update sensitive fields if applicable
      var notification = "";
      var redirect_to = "show";
      if (Object.keys(sensitiveUpdates).length > 0) {
        await dataProvider.update("app_edit_drafts", {
          id: values.client_key,
          data: sensitiveUpdates,
          previousData: {},
        });
        notification += `Draft created for the following fields: ${Object.keys(sensitiveUpdates)}. `;
        redirect_to += "?app_draft_created=true";
      }

      // update non-sensitive fields if applicable
      if (Object.keys(nonSensitiveUpdates).length > 0) {
        await dataProvider.update("apps", { id: values.id, data: nonSensitiveUpdates, previousData: {} });
        notification += `The following fields were immediately updated: ${Object.keys(nonSensitiveUpdates)}`;
      }
      notify(notification);
      redirect(redirect_to, "apps", values.client_key);
    } catch (error) {
      notify("Error: " + error.message, { type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  return <SaveButton onClick={handleSubmit(onSave)} disabled={isSaving} label={isSaving ? "Saving..." : "Save"} />;
};

export const AppEditToolbar = () => (
  <Toolbar>
    <AppSaveButton />
  </Toolbar>
);

export const AppCreate = () => (
  <Grid container direction={"row"}>
    <Grid xs={7}>
      <Create>
        <SimpleForm mode="onBlur">
          <TextInput fullWidth source="client_key" label="client key" validate={required()} />
          <TextInput fullWidth source="app_name" label="App Name" validate={required()} />
          <TextInput fullWidth source="ios_bundle_name" label="iOS bundle name" validate={required()} />
          <TextInput
            fullWidth
            source="ios_environment_properties"
            label="iOS environment properties"
            multiline
            rows={8}
            validate={validateJSON}
          />
          <TextInput fullWidth source="android_package_name" label="Android package name" validate={required()} />
          <TextInput
            fullWidth
            source="android_environment_properties"
            label="Android Environment Properties"
            multiline
            rows={8}
            validate={validateJSON}
          />
          <ArrayInput source="android_signing_fingerprints" label="Android Signing Fingerprints" validate={required()}>
            <SimpleFormIterator inline disableReordering getItemLabel={(index) => `Fingerprint ${index + 1}`}>
              <TextInput source="." helperText={false} fullWidth />
            </SimpleFormIterator>
          </ArrayInput>
          <TextInput fullWidth source="android_genius_scan_key" label="Android Genius Scan Key" />
          <TextInput fullWidth source="ios_genius_scan_key" label="iOS Genius Scan Key" />
          <TextInput fullWidth source="firebase_project_id" label="Firebase project id" validate={required()} />
          <ReferenceInput label="Apple Team Identifier" source="apple_team_identifier" reference="apple_accounts">
            <AutocompleteInput fullWidth optionText="name" suggestionLimit={10} validate={required()} />
          </ReferenceInput>
          <BooleanInput source="apns_production" label="APNS Production" defaultValue={false} validate={required()} />
          <BooleanInput source="is_billable" label="Billable" defaultValue={true} />
        </SimpleForm>
      </Create>
    </Grid>
    <Grid xs={5}>
      <Box px={4} py={2}>
        <AppCreateDoc />
      </Box>
    </Grid>
  </Grid>
);

const transform = (data) => {
  let sanitizedData = data;
  for (const key in data) {
    if (typeof data[key] === "string" && data[key].trim().length === 0) {
      sanitizedData[key] = null;
    }
    if (
      key === "android_signing_fingerprints" &&
      sanitizedData[key] !== null &&
      typeof sanitizedData[key] === "string"
    ) {
      let fingerprints = sanitizedData[key].replace(/ /g, "").split(",");
      sanitizedData[key] = fingerprints.map((fp) => fp.trim());
    }
  }
  return sanitizedData;
};

export const AppEdit = () => {
  const { permissions } = usePermissions();

  return (
    <Edit transform={transform}>
      <SimpleForm
        toolbar={renderIfPermissions(permissions, "apps", "write", <AppEditToolbar />)}
        mode="onBlur"
        redirect="show"
        destroyOnUnregister={true}
        sanitizeEmptyValues={true}
      >
        <TextInput fullWidth source="client_key" label="client key" readOnly />
        <TextInput fullWidth source="app_name" label="App Name" validate={required()} />
        <TextInput fullWidth source="ios_bundle_name" label="iOS bundle name" />
        <TextInput
          fullWidth
          source="ios_environment_properties"
          label="iOS environment properties"
          multiline
          rows={8}
          validate={validateJSON}
        />
        <TextInput fullWidth source="android_package_name" label="Android package name" />
        <TextInput
          fullWidth
          source="android_environment_properties"
          label="Android environment properties"
          multiline
          rows={8}
          validate={validateJSON}
        />
        <TextInput fullWidth source="android_signing_fingerprints" label="Android Signing Fingerprints" />
        <TextInput fullWidth source="android_genius_scan_key" label="Android Genius Scan Key" />
        <TextInput fullWidth source="ios_genius_scan_key" label="iOS Genius Scan Key" />
        <TextInput fullWidth source="firebase_project_id" label="Firebase project id" />
        <ReferenceInput label="Apple Team Identifier" source="apple_team_identifier" reference="apple_accounts">
          <AutocompleteInput fullWidth optionText="name" suggestionLimit={10} validate={required()} />
        </ReferenceInput>
        <BooleanInput source="apns_production" label="APNS Production" defaultValue={false} />
        <BooleanInput source="is_billable" label="Billable" />
      </SimpleForm>
    </Edit>
  );
};

export const AppFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search by key/name" source="app_name" alwaysOn />
  </Filter>
);

export const AppList = (props) => {
  const { isLoading } = useListController();
  if (isLoading) {
    return <LoadingList resource="Apps" />;
  }
  return (
    <List pagination={false} filters={<AppFilter />} exporter={false}>
      <Datagrid key={props.id} rowClick="show" bulkActionButtons={false} size="medium">
        <TextField source="client_key" label="Client Key" sortable={false} />
        <TextField source="app_name" label="Name" sortable={false} />
        <TextField source="ios_bundle_name" label="iOS Bundle" sortable={false} />
        <TextField source="android_package_name" label="Android Bundle" sortable={false} />
      </Datagrid>
    </List>
  );
};

const AppBillingDraftsField = () => {
  const record = useRecordContext();
  const { data, isLoading, error } = useGetList("app_edit_drafts", {
    filter: { id: record.id },
    pagination: { page: 1, perPage: 10 },
  });

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <p>ERROR</p>;
  }

  return <DraftsTable data={data} resource={"app_edit_drafts"} />;
};

export const AppShow = () => {
  const { permissions, isLoading } = usePermissions();
  const [searchParams] = useSearchParams();

  return (
    <Show>
      {isLoading && <CheckingPerms />}
      <TabbedShowLayout spacing={2}>
        <Tab label="app information">
          <Grid container spacing={2}>
            <Grid item xs={7}>
              <Tab label="info">
                {searchParams.get("app_draft_created") === "true" && (
                  <div style={{ "paddingTop?": "10px" }}>
                    <Alert severity="warning">
                      App billing will NOT be modified until approval by a second administrator
                    </Alert>
                  </div>
                )}
                {renderIfPermissions(
                  permissions,
                  "clients",
                  "read_only",
                  <ReferenceField label="Client Key" source="client_key" reference="clients" link="show">
                    <TextField source="key" />
                  </ReferenceField>
                )}
                <TextField source="app_name" label="Name" />
                <TextField source="ios_bundle_name" label="iOS Bundle" />
                <TextField label="iOS Environment Properties" />
                <PrettyJsonField source="ios_environment_properties" />
                <TextField source="android_package_name" label="Android Bundle" />
                <TextField label="Android Environment Properties" />
                <PrettyJsonField source="android_environment_properties" />
                <BooleanField source="apns_production" label="APNS Production" />
                <BooleanField source="is_billable" label="Is billable" />
                <TextField source="apple_team_identifier" label="Apple Team Identifier" />
                <WrappingTextField source="android_signing_fingerprints" label="Android Signing Fingerprints" />
                <WrappingTextField source="android_genius_scan_key" label="Android Genius Scan Key" />
                <WrappingTextField source="ios_genius_scan_key" label="iOS Genius Scan Key" />
              </Tab>
            </Grid>
            {renderIfPermissions(
              permissions,
              "apps",
              "write",
              <Grid item xs={5}>
                <Changelog from="apps" />
              </Grid>
            )}
          </Grid>
        </Tab>
        <Tab label="sensitive fields drafts">
          <AppBillingDraftsField />
        </Tab>
        <Tab label="HERE key permission">
          <TextField label="iOS HERE Permissions" />
          <PrettyJsonField source="ios_here_permissions" />
          <TextField label="Android HERE Permissions" />
          <PrettyJsonField source="android_here_permissions" />
        </Tab>
        <Tab label="Key Switchers">
          {renderIfPermissions(permissions, "apps", "write", <KeySwitchCreateButton />)}
          <ArrayField source="pledges">
            <Datagrid>
              {renderIfPermissions(
                permissions,
                "clients",
                "read_only",
                <ReferenceField reference="clients" source="client_key" link="show">
                  <TextField source="key" />
                </ReferenceField>
              )}
              <DateField showTime label="Last used" source="last_used_at" />
              <TextField label="Key switcher" source="url" />
            </Datagrid>
          </ArrayField>
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
